import type { PageResponseType, ResponseType } from '@/types/common';
import type {
  GitLabBranchCompareResp,
  GitLabProjectRespItem,
  GitLabRepoBranchRespItem,
  GitLabRepoFileResp,
  GitLabRepoTreeRespItem,
} from '@/types/git';
import type { StartCoverageRequest, StartCoverageResponse } from '@/types/precision-new/coverage';
import type { GitBranch, GitCommit, GitGroup, GitMr, GitProject, GitTag, PageQuery } from '@/types/precision-new/git';
import httpUtil from '@/utils/http';

const request = httpUtil.mfd.request;
const prefix = '/v1';

export const gitApi = {
  /**
   * @description 获取项目列表
   */
  getProjects(): ResponseType<GitLabProjectRespItem[]> {
    return request({
      url: `${prefix}/mk-git/projects`,
    });
  },

  /**
   * @description 获取项目分支
   */
  getBranches(params: { projectId: string | number }): ResponseType<GitLabRepoBranchRespItem[]> {
    const { projectId } = params;
    return request({
      url: `${prefix}/mk-git/projects/${projectId}/repository/branches`,
    });
  },

  /**
   * @description 获取项目分支对比 diff
   */
  getCompare(params: { projectId: string | number; from: string; to: string }): ResponseType<GitLabBranchCompareResp> {
    const { projectId } = params;
    const { from, to } = params;
    return request({
      url: `${prefix}/mk-git/projects/${projectId}/repository/compare`,
      params: { from, to },
    });
  },

  /**
   * @description 获取项目仓库目录树
   */
  getTree(params: { projectId: string | number; ref: string }): ResponseType<GitLabRepoTreeRespItem[]> {
    const { projectId } = params;
    const { ref } = params;
    return request({
      url: `${prefix}/mk-git/projects/${projectId}/repository/tree`,
      params: { ref },
    });
  },

  /**
   * @description 获取项目仓库文件
   */
  getFile(params: { projectId: string | number; path: string; ref: string }): ResponseType<GitLabRepoFileResp> {
    const { projectId } = params;
    const { path } = params;
    const { ref } = params;
    return request({
      url: `${prefix}/mk-git/projects/${projectId}/repository/files`,
      params: { path, ref },
    });
  },

  /**
   * @description 获取 Group 列表
   */
  getGroups(params?: PageQuery): PageResponseType<GitGroup> {
    return request({
      url: `${prefix}/precision-testing/git/groups`,
      method: 'GET',
      params,
    });
  },

  /**
   * @description 获取 Group 下的 Projects
   */
  getGroupProjects(params: { groupId: number } & PageQuery): PageResponseType<GitProject> {
    const { groupId, ...rest } = params;
    return request({
      url: `${prefix}/precision-testing/git/groups/${groupId}/projects`,
      method: 'GET',
      params: rest,
    });
  },

  /**
   * @description 获取 Project 信息
   */
  getProjectInfo(projectId: number): ResponseType<GitProject> {
    return request({
      url: `${prefix}/precision-testing/git/projects/${projectId}`,
      method: 'GET',
    });
  },

  /**
   * @description 获取 Project 的 Tags
   * GET /api/v1/precision-testing/git/projects/:projectId/tags
   */
  getProjectTags(params: { projectId: number } & PageQuery): PageResponseType<GitTag> {
    const { projectId, ...rest } = params;
    return request({
      url: `${prefix}/precision-testing/git/projects/${projectId}/tags`,
      method: 'GET',
      params: rest,
    });
  },

  /**
   * @description 获取 Project 的 Branches
   * GET /api/v1/precision-testing/git/projects/:projectId/branches
   */
  getProjectBranches(params: { projectId: number } & PageQuery): PageResponseType<GitBranch> {
    const { projectId, ...rest } = params;
    return request({
      url: `${prefix}/precision-testing/git/projects/${projectId}/branches`,
      method: 'GET',
      params: rest,
    });
  },

  /**
   * @description 获取 Project 的 Commits（列表/搜索模式）
   * GET /api/v1/precision-testing/git/projects/:projectId/commits
   */
  getProjectCommits(
    params:
      | { projectId: number; search: string; pageIndex?: number; pageSize?: number }
      | { projectId: number; refName?: string; since?: string; until?: string; pageIndex?: number; pageSize?: number },
  ): PageResponseType<GitCommit> {
    const { projectId, ...rest } = params as any;
    return request({
      url: `${prefix}/precision-testing/git/projects/${projectId}/commits`,
      method: 'GET',
      params: rest,
    });
  },

  /**
   * @description 获取 Project 的 Merge Requests
   * GET /api/v1/precision-testing/git/projects/:projectId/mrs
   */
  getProjectMrs(params: {
    projectId: number;
    search?: string;
    authorUsername?: string;
    state?: string;
    pageIndex?: number;
    pageSize?: number;
  }): PageResponseType<GitMr> {
    const { projectId, ...rest } = params;
    return request({
      url: `${prefix}/precision-testing/git/projects/${projectId}/mrs`,
      method: 'GET',
      params: rest,
    });
  },

  /**
   * @description 开始计算覆盖率
   * POST /api/v1/precision-testing/coverage/start
   */
  startCoverage(params: StartCoverageRequest): ResponseType<StartCoverageResponse> {
    return request({
      url: `${prefix}/precision-testing/coverage/start`,
      method: 'POST',
      data: params,
    });
  },

  /**
   * @description 变动确认预览（对比预览）
   * POST /api/v1/precision-testing/git/projects/:projectId/compare
   * 其余请求入参与 StartCoverageRequest 一致（不含/忽略 projectId）
   */
  comparePreview(params: { projectId: number } & Partial<StartCoverageRequest>): ResponseType<any> {
    const { projectId, ...data } = params;
    return request({
      url: `${prefix}/precision-testing/git/projects/${projectId}/compare`,
      method: 'POST',
      data,
    });
  },
};
